import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

export default function AudioPlayer({ id, src, title, credit, tag }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    const onTime = () => setTime(a.currentTime);
    const onEnd = () => {
      setPlaying(false);
      setTime(0);
    };
    const onMeta = () => setDuration(a.duration);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("loadedmetadata", onMeta);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("loadedmetadata", onMeta);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      document.querySelectorAll("audio").forEach((el) => {
        if (el !== a) el.pause();
      });
      a.play();
      setPlaying(true);
    }
  };

  const seek = (e) => {
    if (!duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audioRef.current.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const pct = duration ? (time / duration) * 100 : 0;

  return (
    <div className="glass rounded-3xl p-6 lg:p-7 relative overflow-hidden w-full max-w-full min-w-0" data-testid={`audio-card-${id}`}>
      <span className="absolute top-5 right-5 font-mono text-[9px] tracking-[0.25em] text-horange border border-horange/40 rounded-full px-3 py-1" data-testid={`audio-tag-${id}`}>
        {tag}
      </span>
      <audio ref={audioRef} src={src} preload="metadata" className="hidden" data-testid={`audio-element-${id}`} />
      <div className="flex items-center gap-5">
        <button
          onClick={toggle}
          data-testid={`audio-play-${id}`}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          className="w-14 h-14 shrink-0 rounded-full bg-hpink hover:bg-horange transition-colors duration-300 flex items-center justify-center text-white glow-pink"
        >
          {playing ? <Pause size={20} fill="currentColor" /> : <Play size={20} fill="currentColor" className="ml-0.5" />}
        </button>
        <div className="min-w-0 pr-20">
          <h4 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight truncate">{title}</h4>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-hmut mt-1 truncate">{credit}</p>
        </div>
      </div>
      <div className="mt-5 flex items-center gap-4">
        <span className="font-mono text-[10px] text-hdim w-9" data-testid={`audio-time-${id}`}>
          {fmt(time)}
        </span>
        <div onClick={seek} className="relative flex-1 h-2 rounded-full bg-white/10 cursor-pointer" data-testid={`audio-progress-${id}`}>
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-hpink to-horange pointer-events-none"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="font-mono text-[10px] text-hdim w-9 text-right">{fmt(duration)}</span>
      </div>
      <div className="mt-4 flex items-end gap-1 h-5" aria-hidden="true">
        {[...Array(28)].map((_, i) => (
          <span
            key={i}
            className={`w-1 rounded-full ${playing ? "eq-bar" : "bg-white/10"}`}
            style={{ height: `${6 + ((i * 7) % 14)}px`, animationDelay: `${i * 0.06}s`, transformOrigin: "bottom" }}
          />
        ))}
        <span className="ml-auto font-mono text-[9px] tracking-[0.2em] text-hdim">75s PREVIEW</span>
      </div>
    </div>
  );
}
